import { ReactNode } from 'react';
import { ListItem, ListItemText, Avatar, Dialog, ListItemButton, useTheme, Button } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Connection } from '@domain/connection/models/Connection';
import { APP_CONFIG } from '../../../../config/app.config';
import { ConnectionAuthStatus } from '@features/connections/generic/components/ConnectionAuthStatus';
import ShopifyConnectForm from './ShopifyConnectForm';
import ShopifyConnectionSettings from './ShopifyConnectionSettings';
import { Icon } from '@shared/icons';

export interface ShopifyConnectionListItemProps {
    connection: Connection;
    onClick?: (settings: ReactNode) => void;
}

export const ShopifyConnectionListItem = ({ connection, onClick }: ShopifyConnectionListItemProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [reconnectOpen, setReconnectOpen] = useState(false);
    const needsSetup = connection.authorized && !connection.active;

    const handleRowClick = onClick
        ? () => {
            if (needsSetup) {
                navigate(`/company/connections/${connection.id}/setup`);
                return;
            }
            onClick(<ShopifyConnectionSettings connectionId={connection.id} />);
        }
        : undefined;

    const content = (
        <>
            <Avatar
                sx={{ width: 60, height: 60, border: 1, borderColor: APP_CONFIG.ui.theme.colors.border, backgroundColor: theme.palette.background.paper }}
                variant="rounded"
                alt={connection.connector.type}
                slotProps={{
                    img: {
                        style: { objectFit: 'contain' }
                    }
                }}
                src={connection.connector.image} />

            <ListItemText primary={connection.name} secondary={connection.connector.type} />

            <ConnectionAuthStatus
                authorized={connection.authorized}
                active={connection.active}
                onReconnect={() => setReconnectOpen(true)}
            />

            {needsSetup && (
                <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/company/connections/${connection.id}/setup`);
                    }}
                >
                    Finish setup
                </Button>
            )}

            {onClick && (
                <Icon icon="ChevronRight" color={theme.palette.text.secondary} />
            )}

            <Dialog open={reconnectOpen} onClose={() => setReconnectOpen(false)} maxWidth="sm" fullWidth>
                <ShopifyConnectForm
                    connectionId={connection.id}
                    onClose={() => setReconnectOpen(false)}
                />
            </Dialog>
        </>
    );

    if (handleRowClick) {
        return (
            <ListItemButton sx={{ p: 2, gap: 2 }} onClick={handleRowClick} key={connection.id}>
                {content}
            </ListItemButton>
        );
    }

    return (
        <ListItem sx={{ p: 2, gap: 2 }} key={connection.id}>
            {content}
        </ListItem>
    );
};
